import { Card, Box, Text, Button, Spinner } from '@nimbus-ds/components';
import { buildLocaleFromLanguageCountry } from 'App/i18n/saveLanguage';
import { useGetLanguage, useGetCountry } from 'domains/Auth/hooks';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';

interface SummaryCardProps {
  storeName: string;
  domainName: string;
  price: {
    amount: number;
    currency: string;
  };
  onPayment: () => void;
  isLoading?: boolean;
}

export function SummaryCard({
  storeName,
  domainName,
  price,
  onPayment,
  isLoading = false,
}: Readonly<SummaryCardProps>) {
  const t = useTranslationConfigurations('domains.checkout.summary');
  const language = useGetLanguage();
  const country = useGetCountry();
  const locale = buildLocaleFromLanguageCountry(language, country);

  const formattedPrice = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency,
  }).format(price.amount);

  return (
    <Card padding="none">
      <Box padding="4" display="flex" flexDirection="column" gap="4">
        <Box>
          <Text fontSize="highlight" color="neutral-textHigh">
            {t('title')}
          </Text>
        </Box>

        <Box display="flex" flexDirection="column">
          <Text fontSize="base" color="neutral-textLow">
            {storeName}
          </Text>
          <Text fontSize="base" fontWeight="bold" color="neutral-textHigh">
            {domainName}
          </Text>
          <Text fontSize="base" color="neutral-textLow">
            {formattedPrice}
          </Text>
        </Box>

        <Box>
          <Text fontSize="caption" color="neutral-textLow">
            {t('nonRefundableNotice')}
          </Text>
        </Box>
      </Box>

      <Box
        padding="4"
        backgroundColor="primary-surface"
        display="flex"
        flexDirection="column"
        gap="4"
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="base" fontWeight="bold" color="neutral-textHigh">
            {t('total')}
          </Text>
          <Text fontSize="base" fontWeight="bold" color="neutral-textHigh">
            {formattedPrice}
          </Text>
        </Box>

        <Button
          appearance="primary"
          onClick={onPayment}
          disabled={isLoading}
          fullWidth
        >
          {isLoading && <Spinner color="currentColor" size="small" />}
          {t('payButton')}
        </Button>
      </Box>
    </Card>
  );
}
